import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trophy, Zap, PartyPopper, Gift, Loader2, Home, Sparkles, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_ICON = {
  squad_legend: Trophy, fast_paymaster: Zap, party_starter: PartyPopper, contributor: Gift,
};

const Leaderboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("payers");

  useEffect(() => {
    axios.get(`${API_URL}/api/party-vibes/leaderboard`)
      .then(r => setData(r.data))
      .catch(() => setData({ top_hosts: [], top_payers: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#E83E8C]" size={32}/></div>;

  const rows = tab === "payers" ? (data.top_payers || []) : (data.top_hosts || []);

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="leaderboard-page">
      <div className="bg-gradient-to-br from-[#1E1B4B] via-[#3b0764] to-[#FF9F1C] px-5 py-10 text-white">
        <button onClick={() => navigate("/")} className="text-white/80 text-xs mb-4 flex items-center gap-1"><Home size={12}/> DoozyGo</button>
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={20} className="text-[#FFD700]"/>
          <span className="uppercase text-xs tracking-widest font-semibold opacity-80">Squad Leaderboard</span>
        </div>
        <h1 className="text-4xl font-black font-['Outfit'] leading-tight">
          Who's running<br/>the <span className="text-[#FFD700]">party game?</span> 🔥
        </h1>
        <p className="mt-3 text-sm opacity-80">Fastest payers get the 🏆 Squad Legend badge · Visible on every pool invite.</p>
      </div>

      <div className="max-w-2xl mx-auto p-5">
        {/* Tabs */}
        <div className="flex gap-2 mb-5 bg-white rounded-xl p-1 shadow-sm">
          <button onClick={() => setTab("payers")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${tab === "payers" ? "bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white" : "text-gray-500"}`} data-testid="tab-top-payers">
            ⚡ Fastest Payers
          </button>
          <button onClick={() => setTab("hosts")} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition ${tab === "hosts" ? "bg-gradient-to-r from-[#E83E8C] to-orange-500 text-white" : "text-gray-500"}`} data-testid="tab-top-hosts">
            🎉 Top Hosts
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-sm text-gray-500 border border-dashed">
            No pool activity yet. Create a pool to get on the board! 🎊
            <button onClick={() => navigate("/plan-with-friends")} className="block mx-auto mt-3 px-4 py-2 rounded-lg bg-[#E83E8C] text-white text-xs font-bold">
              Plan a Party →
            </button>
          </div>
        ) : (
          <ol className="space-y-2">
            {rows.map((r, i) => {
              const b = r.badge || {};
              const Icon = TIER_ICON[b.tier] || Sparkles;
              return (
                <li key={r.phone} className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition flex items-center gap-3" data-testid={`leader-row-${i}`}>
                  {/* Rank */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black ${
                    i === 0 ? "bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-white" :
                    i === 1 ? "bg-gradient-to-br from-gray-300 to-gray-400 text-white" :
                    i === 2 ? "bg-gradient-to-br from-orange-400 to-orange-600 text-white" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {i + 1}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-bold text-[#1E1B4B] truncate">{r.name || r.phone}</p>
                      {b.tier && (
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1"
                          style={{ backgroundColor: `${b.color}22`, color: b.color }}
                        >
                          {b.emoji} {b.label}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {tab === "payers"
                        ? `${r.pools_paid} pool(s) paid · avg ${r.avg_pay_minutes != null ? `${r.avg_pay_minutes}min` : "—"} · ₹${r.total_contributed?.toLocaleString() || 0}`
                        : `${r.pools_hosted} pool(s) hosted · squad pooled ₹${r.total_contributed?.toLocaleString() || 0}`}
                    </p>
                  </div>
                  <Icon size={18} className="text-gray-300"/>
                </li>
              );
            })}
          </ol>
        )}

        {/* CTA card */}
        <div className="mt-6 bg-gradient-to-br from-[#1E1B4B] to-[#E83E8C] rounded-2xl p-5 text-white text-center">
          <p className="text-lg font-black font-['Outfit']">Want the 🏆 badge on your next invite?</p>
          <p className="text-xs opacity-80 mt-1 mb-4">Pay within 15 min on 2+ pools to earn "Squad Legend".</p>
          <button onClick={() => navigate("/plan-with-friends")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-[#1E1B4B] font-bold text-sm">
            Plan My Party <ArrowRight size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
